import { useCallback, useEffect, useState } from "react";
import { Boxes, Ruler, ListFilter, Inbox, SlidersHorizontal, Link2, Wallet } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Badge } from "@/components/ui/badge";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { wmMessage } from "@/lib/wmError";
import { ItemsAdmin } from "@/components/waste-meals/admin/ItemsAdmin";
import { UnitsAdmin } from "@/components/waste-meals/admin/UnitsAdmin";
import { ReasonsAdmin } from "@/components/waste-meals/admin/ReasonsAdmin";
import { ItemRequestsAdmin } from "@/components/waste-meals/admin/ItemRequestsAdmin";
import { PolicyCard } from "@/components/waste-meals/admin/PolicyCard";
import { RecipeLinksAdmin } from "@/components/waste-meals/admin/RecipeLinksAdmin";
import { MissingCostAdmin } from "@/components/waste-meals/admin/MissingCostAdmin";

const C = OPS.wm.admin;

const SECTIONS = [
    { key: "items", icon: Boxes, label: "wm_admin_items" },
    { key: "units", icon: Ruler, label: "wm_admin_units" },
    { key: "reasons", icon: ListFilter, label: "wm_admin_reasons" },
    { key: "requests", icon: Inbox, label: "wm_admin_requests" },
    { key: "policy", icon: SlidersHorizontal, label: "wm_admin_policy" },
    // 2026-10-16 (D5): recipe links → cost snapshot at save, and what is still uncosted
    { key: "links", icon: Link2, label: "wm_admin_links" },
    { key: "missing", icon: Wallet, label: "wm_admin_missing" },
];

/**
 * «إدارة الأصناف» — spec level 4 (مدير النظام), `wm_admin`.
 *
 * The catalogue must never live in code (spec: «يجب عدم جعل الأصناف ثابتة داخل
 * الكود»), so items, units and reasons are all editable here. The requests
 * section is the other half of the employees' «لم أجد الصنف» button, which
 * until now notified admins with nowhere to act.
 */
export function AdminTab() {
    const { t, lang } = useI18n();
    const [section, setSection] = useState("items");
    const [units, setUnits] = useState([]);
    const [openRequests, setOpenRequests] = useState(0);
    const [loading, setLoading] = useState(true);

    const loadUnits = useCallback(async () => {
        try {
            const r = await api.get("/waste-meals/units");
            setUnits(r.data.rows || []);
        } catch (e) {
            toast.error(wmMessage(e, lang) || t("wm_offline_error"));
        } finally {
            setLoading(false);
        }
    }, [lang, t]);

    useEffect(() => { loadUnits(); }, [loadUnits]);
    useEffect(() => {
        api.get("/waste-meals/item-requests", { params: { status: "open" } })
            .then((r) => setOpenRequests(r.data.open_count || 0))
            .catch(() => {});
    }, [section]);

    if (loading) return <InlineLoader label={t("loading")} />;

    return (
        <div data-testid={C.tab} className="space-y-4">
            <div className="flex flex-wrap gap-1 rounded-full border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-1">
                {SECTIONS.map((s) => {
                    const Icon = s.icon;
                    const on = section === s.key;
                    return (
                        <button key={s.key} type="button" data-testid={C.section(s.key)}
                                onClick={() => setSection(s.key)}
                                className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors duration-150 ${
                                    on
                                        ? "bg-[color:var(--brand-orange)] text-white"
                                        : "text-[color:var(--ink-muted)] hover:bg-[color:var(--brand-orange-soft)] hover:text-[color:var(--ink)]"
                                }`}>
                            <Icon className="h-3.5 w-3.5" />
                            {t(s.label)}
                            {s.key === "requests" && openRequests ? (
                                <Badge data-testid={C.requestsBadge}
                                       className="ms-0.5 h-4 min-w-4 justify-center bg-[color:var(--danger)] px-1 text-[10px] text-white">
                                    <bdi dir="ltr">{openRequests}</bdi>
                                </Badge>
                            ) : null}
                        </button>
                    );
                })}
            </div>

            {section === "items" ? <ItemsAdmin units={units} /> : null}
            {section === "units" ? <UnitsAdmin units={units} onChanged={loadUnits} /> : null}
            {section === "reasons" ? <ReasonsAdmin /> : null}
            {section === "requests" ? (
                <ItemRequestsAdmin onCountChange={setOpenRequests} />
            ) : null}
            {section === "policy" ? <PolicyCard /> : null}
            {section === "links" ? <RecipeLinksAdmin /> : null}
            {section === "missing" ? <MissingCostAdmin onOpenLinks={() => setSection("links")} /> : null}
        </div>
    );
}
